// src/ApiPanel/pages/ca/CashDeclaration.jsx
// Cash Declaration: the list, the submit form, and the reviewer's approve /
// reject action.
//
// Two things the UI enforces because getting them wrong wastes the user's
// time, not because the UI is the control — the backend refuses both
// independently:
//   * closing = opening + collected − deposited. Shown live as the user
//     types, so they see the arithmetic before they submit rather than after
//     a rejected request.
//   * you cannot review your own declaration. The Approve/Reject buttons are
//     hidden on your own rows; the server still checks.
import React, { useMemo, useState } from "react";
import {
  Badge, Card, DataTable, DateField, ExportButton, FilterBar, Modal, Notice,
  Pagination, ReportPage, SelectField, Stat, StatGrid, count, daysAgoISO,
  money, prettyDate, rupees, todayISO,
} from "../../components/reports/ReportKit";
import { useAction, useReportQuery } from "../../hooks/useReportQuery";
import { apiPanelDownload, qs } from "../../utils/apiPanelFetch";
import { API_PANEL_ENDPOINTS } from "../../constants/apiPanelAuth";
import { useApiPanelSession } from "../../context/ApiPanelSessionContext";

const STATUS_OPTIONS = [
  { value: "SUBMITTED", label: "Submitted" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

const REVIEWER_ROLES = new Set(["Admin", "Main Admin", "White label"]);

const defaults = () => ({
  start_date: daysAgoISO(29),
  end_date: todayISO(),
  decl_status: "",
  page: 1,
});

export default function CashDeclaration() {
  const { user } = useApiPanelSession();
  const [filters, setFilters] = useState(defaults);
  const [applied, setApplied] = useState(defaults);
  const [exporting, setExporting] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [review, setReview] = useState(null);

  const { data, loading, error, reload } = useReportQuery(
    API_PANEL_ENDPOINTS.CA_CASH_DECLARATION,
    applied
  );
  const action = useAction();

  const canReview = REVIEWER_ROLES.has(user?.role);
  const totals = data?.totals || {};
  const rows = data?.data || [];

  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiPanelDownload(
        `${API_PANEL_ENDPOINTS.CA_CASH_DECLARATION}${qs({ ...applied, export: "csv", page: undefined })}`,
        `cash_declaration_${applied.start_date}_${applied.end_date}.csv`
      );
    } catch {
      /* download failed; list unaffected */
    } finally {
      setExporting(false);
    }
  };

  const submitReview = async (decision) => {
    const res = await action.run(
      API_PANEL_ENDPOINTS.CA_CASH_DECLARATION_ACTION,
      { action: "review", id: review.id, decision, review_remark: review.remark || "" },
      { form: true }
    );
    if (res) {
      setReview(null);
      reload();
    }
  };

  return (
    <ReportPage
      title="Cash Declaration"
      subtitle="Daily cash-in-hand declared by retailers, and the variance against what the ledger expects."
      actions={
        <>
          <button type="button" className="apip-btn apip-btn-primary" onClick={() => setShowSubmit(true)}>
            <i className="iconoir-plus" /> New Declaration
          </button>
          <ExportButton onExport={handleExport} busy={exporting} />
        </>
      }
    >
      <Card>
        <FilterBar
          onApply={() => setApplied({ ...filters, page: 1 })}
          onReset={() => {
            const fresh = defaults();
            setFilters(fresh);
            setApplied(fresh);
          }}
          busy={loading}
        >
          <DateField label="From" value={filters.start_date} onChange={set("start_date")} max={filters.end_date} />
          <DateField label="To" value={filters.end_date} onChange={set("end_date")} min={filters.start_date} max={todayISO()} />
          <SelectField label="Status" value={filters.decl_status} onChange={set("decl_status")} options={STATUS_OPTIONS} />
        </FilterBar>
      </Card>

      <StatGrid>
        <Stat label="Declarations" value={count(totals.declarations)} />
        <Stat label="Cash Collected" value={rupees(totals.cash_collected)} />
        <Stat label="Cash Deposited" value={rupees(totals.cash_deposited)} />
        <Stat label="Closing Cash" value={rupees(totals.closing_cash)} tone="brand" />
        <Stat
          label="Variance"
          value={rupees(totals.variance)}
          tone={Math.abs(Number(totals.variance || 0)) > 0 ? "warn" : "ok"}
          sub="Declared minus ledger"
        />
      </StatGrid>

      {action.message && <Notice tone="ok">{action.message}</Notice>}
      {action.error && <Notice tone="bad">{action.error}</Notice>}

      <Card title="Declarations">
        <DataTable
          columns={[
            { key: "declared_for", label: "For Date", render: (r) => prettyDate(r.declared_for) },
            {
              key: "user_name",
              label: "Retailer",
              render: (r) => (
                <span className="apip-party">
                  {r.user_name || "—"}
                  <small className="apip-mono">{r.user_phone}</small>
                </span>
              ),
            },
            { key: "opening_cash", label: "Opening", numeric: true, render: (r) => money(r.opening_cash) },
            { key: "cash_collected", label: "Collected", numeric: true, render: (r) => money(r.cash_collected) },
            { key: "cash_deposited", label: "Deposited", numeric: true, render: (r) => money(r.cash_deposited) },
            { key: "closing_cash", label: "Closing", numeric: true, render: (r) => <strong>{money(r.closing_cash)}</strong> },
            { key: "system_cash", label: "Ledger", numeric: true, render: (r) => money(r.system_cash) },
            {
              key: "variance",
              label: "Variance",
              numeric: true,
              render: (r) => {
                const v = Number(r.variance || 0);
                if (v === 0) return <span className="apip-ok-txt">0.00</span>;
                return <span className={v > 0 ? "apip-warn-txt" : "apip-bad-txt"}>{money(r.variance)}</span>;
              },
            },
            { key: "status", label: "Status", render: (r) => <Badge value={r.status} /> },
            {
              key: "actions",
              label: "",
              width: 150,
              render: (r) => {
                const isMine = r.user_id === user?.id;
                if (!canReview || r.status !== "SUBMITTED") return null;
                if (isMine) {
                  return <span className="apip-hint">Needs another reviewer</span>;
                }
                return (
                  <button
                    type="button"
                    className="apip-btn apip-btn-sm apip-btn-ghost"
                    onClick={() => setReview({ ...r, remark: "" })}
                  >
                    Review
                  </button>
                );
              },
            },
          ]}
          rows={rows}
          loading={loading}
          error={error}
          empty="No declarations in this range."
          rowKey={(r) => r.id}
        />
        <Pagination meta={data?.pagination} onPage={(page) => setApplied((a) => ({ ...a, page }))} />
      </Card>

      <SubmitDeclarationModal
        open={showSubmit}
        onClose={() => setShowSubmit(false)}
        onDone={() => {
          setShowSubmit(false);
          reload();
        }}
      />

      <Modal
        open={!!review}
        title={`Review declaration — ${review?.user_name || ""}`}
        onClose={() => setReview(null)}
        footer={
          <>
            <button
              type="button"
              className="apip-btn apip-btn-ghost apip-btn-danger"
              disabled={action.busy}
              onClick={() => submitReview("REJECTED")}
            >
              Reject
            </button>
            <button
              type="button"
              className="apip-btn apip-btn-primary"
              disabled={action.busy}
              onClick={() => submitReview("APPROVED")}
            >
              {action.busy ? "Saving…" : "Approve"}
            </button>
          </>
        }
      >
        {review && (
          <>
            <dl className="apip-kv apip-kv-2">
              <dt>For date</dt><dd>{prettyDate(review.declared_for)}</dd>
              <dt>Opening</dt><dd className="apip-num">{rupees(review.opening_cash)}</dd>
              <dt>Collected</dt><dd className="apip-num">{rupees(review.cash_collected)}</dd>
              <dt>Deposited</dt><dd className="apip-num">{rupees(review.cash_deposited)}</dd>
              <dt>Closing declared</dt><dd className="apip-num"><strong>{rupees(review.closing_cash)}</strong></dd>
              <dt>Ledger expects</dt><dd className="apip-num">{rupees(review.system_cash)}</dd>
              <dt>Variance</dt><dd className="apip-num">{rupees(review.variance)}</dd>
              <dt>Bank</dt><dd>{review.bank_name || "—"}</dd>
              <dt>Deposit ref</dt><dd className="apip-mono">{review.deposit_ref || "—"}</dd>
              <dt>Slip attached</dt><dd>{review.has_slip ? "Yes" : "No"}</dd>
            </dl>
            {review.remarks && <Notice tone="info">Retailer note: {review.remarks}</Notice>}
            {Math.abs(Number(review.variance || 0)) > 0 && (
              <Notice tone="warn">
                The declared closing cash does not match the ledger. Approving accepts the
                difference — say why in the remark so the trail explains itself later.
              </Notice>
            )}
            <label className="apip-field">
              <span>Review remark</span>
              <textarea
                className="apip-input"
                rows={3}
                maxLength={250}
                value={review.remark}
                onChange={(e) => setReview((r) => ({ ...r, remark: e.target.value }))}
                placeholder="Why you are approving or rejecting this"
              />
            </label>
            {action.error && <Notice tone="bad">{action.error}</Notice>}
          </>
        )}
      </Modal>
    </ReportPage>
  );
}

function SubmitDeclarationModal({ open, onClose, onDone }) {
  const action = useAction();
  const [form, setForm] = useState({
    declared_for: todayISO(),
    opening_cash: "",
    cash_collected: "",
    cash_deposited: "",
    bank_name: "",
    deposit_ref: "",
    remarks: "",
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Computed, never typed — so the number that gets submitted is always the
  // one the arithmetic implies, and the field cannot silently disagree.
  const closing = useMemo(() => {
    const n = (v) => {
      const parsed = Number(v);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    return n(form.opening_cash) + n(form.cash_collected) - n(form.cash_deposited);
  }, [form.opening_cash, form.cash_collected, form.cash_deposited]);

  const submit = async (e) => {
    e.preventDefault();
    const res = await action.run(
      API_PANEL_ENDPOINTS.CA_CASH_DECLARATION_ACTION,
      { action: "submit", ...form, closing_cash: closing.toFixed(2) },
      { form: true }
    );
    if (res) onDone();
  };

  return (
    <Modal
      open={open}
      title="New cash declaration"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="apip-btn apip-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="apip-cash-form"
            className="apip-btn apip-btn-primary"
            disabled={action.busy || closing < 0}
          >
            {action.busy ? "Submitting…" : "Submit"}
          </button>
        </>
      }
    >
      <form id="apip-cash-form" onSubmit={submit}>
        <div className="apip-form-grid">
          <label className="apip-field">
            <span>For date</span>
            <input type="date" className="apip-input" required max={todayISO()} value={form.declared_for} onChange={set("declared_for")} />
          </label>
          <label className="apip-field">
            <span>Opening cash</span>
            <input type="number" className="apip-input" required min="0" step="0.01" value={form.opening_cash} onChange={set("opening_cash")} />
          </label>
          <label className="apip-field">
            <span>Cash collected</span>
            <input type="number" className="apip-input" required min="0" step="0.01" value={form.cash_collected} onChange={set("cash_collected")} />
          </label>
          <label className="apip-field">
            <span>Cash deposited</span>
            <input type="number" className="apip-input" required min="0" step="0.01" value={form.cash_deposited} onChange={set("cash_deposited")} />
          </label>
          <label className="apip-field">
            <span>Bank name</span>
            <input type="text" className="apip-input" maxLength={120} value={form.bank_name} onChange={set("bank_name")} />
          </label>
          <label className="apip-field">
            <span>Deposit reference</span>
            <input type="text" className="apip-input" maxLength={60} value={form.deposit_ref} onChange={set("deposit_ref")} />
          </label>
        </div>

        <div className={`apip-computed${closing < 0 ? " bad" : ""}`}>
          <span>Closing cash</span>
          <strong>{rupees(closing.toFixed(2))}</strong>
          <small>opening + collected − deposited</small>
        </div>
        {closing < 0 && (
          <Notice tone="bad">
            That works out to negative cash in hand. Check the deposited figure before submitting.
          </Notice>
        )}

        <label className="apip-field">
          <span>Remarks</span>
          <textarea className="apip-input" rows={2} maxLength={250} value={form.remarks} onChange={set("remarks")} />
        </label>

        {action.error && <Notice tone="bad">{action.error}</Notice>}
      </form>
    </Modal>
  );
}
